import API_URL from '../api';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';

const TYPES = [
  { value: 'dj_set',       label: 'DJ Set' },
  { value: 'concert',      label: 'Concert' },
  { value: 'live_band',    label: 'Live Band' },
  { value: 'festival_set', label: 'Festival Set' },
  { value: 'rave',         label: 'Rave' },
  { value: 'other',        label: 'Other' },
];

export default function CreateSet() {
  const [perfType, setPerfType] = useState('dj_set');
  const [title, setTitle]       = useState('');
  const [artistName, setArtistName] = useState('');
  const [djId, setDjId]         = useState('');
  const [genre, setGenre]       = useState('');
  const [location, setLocation] = useState('');
  const [venueId, setVenueId]   = useState(null);
  const [venueSuggestions, setVenueSuggestions] = useState([]);
  const [showVenueDrop, setShowVenueDrop] = useState(false);
  const [duration, setDuration] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [myDjs, setMyDjs]       = useState([]);
  const [loadingDjs, setLoadingDjs] = useState(true);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [artistMatch, setArtistMatch] = useState(null);
  const [checkingMatch, setCheckingMatch] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const venueDebounce = useRef(null);
  const venueInputRef = useRef(null);

  const isDjSet = perfType === 'dj_set';

  const handleVenueInput = (val) => {
    setLocation(val);
    setVenueId(null);
    clearTimeout(venueDebounce.current);
    if (val.length < 2) { setVenueSuggestions([]); setShowVenueDrop(false); return; }
    venueDebounce.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/venues/search?q=${encodeURIComponent(val)}`);
        setVenueSuggestions(res.data);
        setShowVenueDrop(true);
      } catch {}
    }, 250);
  };

  const selectVenue = (v) => {
    setLocation(`${v.name}, ${v.city}`);
    setVenueId(v.id);
    setVenueSuggestions([]);
    setShowVenueDrop(false);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/api/artist-requests/my-artist-profiles`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setMyDjs(res.data);
        const preselect = searchParams.get('dj');
        if (preselect) setDjId(preselect);
        else if (res.data.length === 1) setDjId(String(res.data[0].id));
        setLoadingDjs(false);
      })
      .catch(() => setLoadingDjs(false));
  }, []); // eslint-disable-line

  const buildPayload = (overrides = {}) => ({
    performance_type: perfType,
    title,
    genre: genre || null,
    location: location || null,
    venue_id: venueId || null,
    duration: duration ? parseInt(duration) : null,
    video_url: videoUrl || null,
    ...(isDjSet ? { dj_id: djId } : { artist_name: artistName }),
    ...overrides,
  });

  const submitSet = async (overrides = {}) => {
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API_URL}/api/sets`, buildPayload(overrides),
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      navigate(`/set/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isDjSet || !artistName.trim()) return submitSet();

    setCheckingMatch(true);
    try {
      const res = await axios.get(`${API_URL}/api/artists/match?name=${encodeURIComponent(artistName)}`);
      setCheckingMatch(false);
      if (res.data.match && !res.data.exact) {
        setArtistMatch(res.data.match);
        return;
      }
    } catch {
      setCheckingMatch(false);
    }
    submitSet();
  };

  const confirmMatch = () => {
    const matched = artistMatch.name;
    setArtistMatch(null);
    submitSet({ artist_name: matched });
  };

  const rejectMatch = () => {
    setArtistMatch(null);
    submitSet({ force_new_artist: true });
  };

  const inp = "w-full bg-white/[0.04] border border-white/10 focus:border-brand-500 rounded-xl px-4 py-2.5 text-white placeholder-gray-600 text-sm outline-none transition-colors duration-200";

  // DJ-set with no owned DJ profiles → must create one first
  const blockedNoDj = isDjSet && !loadingDjs && myDjs.length === 0;

  return (
    <div className="min-h-[85vh] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <Link to="/my-artists" className="text-gray-500 hover:text-brand-400 text-sm transition-colors block mb-6">← My Artists</Link>
        <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl p-7 shadow-2xl backdrop-blur-sm">
          <h1 className="text-2xl font-extrabold text-white mb-1">Add Live Music</h1>
          <p className="text-gray-500 text-sm mb-6">Add a performance for the community to rate</p>

          {error && (
            <div className="mb-5 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Performance type */}
            <div>
              <label className="block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1.5">Type</label>
              <select value={perfType} onChange={e => setPerfType(e.target.value)} className={inp + ' appearance-none'}>
                {TYPES.map(t => <option key={t.value} value={t.value} className="bg-[#111]">{t.label}</option>)}
              </select>
            </div>

            <Field label="Title" inp={inp} value={title} onChange={setTitle}
              placeholder={isDjSet ? 'Summer Vibes Mix' : 'Eras Tour — Night 3'} required />

            {/* DJ set → DJ picker; otherwise → artist name */}
            {isDjSet ? (
              <div>
                <label className="block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1.5">Artist</label>
                {loadingDjs ? (
                  <div className="h-10 bg-white/[0.04] border border-white/10 rounded-xl animate-pulse" />
                ) : myDjs.length === 0 ? (
                  <p className="text-gray-500 text-sm">No Artist profiles yet.</p>
                ) : (
                  <select value={djId} onChange={e => setDjId(e.target.value)} required className={inp + ' appearance-none'}>
                    <option value="" className="bg-[#111]">Select an Artist</option>
                    {myDjs.map(dj => (
                      <option key={dj.id} value={dj.id} className="bg-[#111]">{dj.name}{dj.verified ? ' ✅' : ''}</option>
                    ))}
                  </select>
                )}
              </div>
            ) : (
              <Field label="Artist / Band Name" inp={inp} value={artistName}
                onChange={val => { setArtistName(val); setArtistMatch(null); }}
                placeholder="Taylor Swift" required />
            )}

            <Field label="Genre" inp={inp} value={genre} onChange={setGenre} placeholder="Pop, Techno, Rock…" optional />

            {/* Venue autocomplete */}
            <div className="relative">
              <label className="block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                Venue <span className="text-gray-700 normal-case tracking-normal font-normal">(optional)</span>
              </label>
              <input
                ref={venueInputRef}
                type="text"
                value={location}
                onChange={e => handleVenueInput(e.target.value)}
                onFocus={() => venueSuggestions.length > 0 && setShowVenueDrop(true)}
                onBlur={() => setTimeout(() => setShowVenueDrop(false), 150)}
                placeholder="Start typing a venue name…"
                className={inp + (venueId ? ' border-[#00D9FF]/50' : '')}
                autoComplete="off"
              />
              {venueId && (
                <span className="absolute right-3 top-[38px] text-[#00D9FF] text-xs pointer-events-none">✓ matched</span>
              )}
              {showVenueDrop && venueSuggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-[#0d0d0f] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
                  {venueSuggestions.map(v => (
                    <button
                      key={v.id}
                      type="button"
                      onMouseDown={() => selectVenue(v)}
                      className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-white/[0.06] transition-colors text-left"
                    >
                      <span className="text-white text-sm">{v.name}</span>
                      <span className="text-gray-500 text-xs ml-3 flex-shrink-0">{v.city}, {v.country}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Field label="Duration (minutes)" inp={inp} type="number" value={duration} onChange={setDuration} placeholder="90" optional />

            <div>
              <label className="block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                YouTube Video ID <span className="text-gray-700 normal-case tracking-normal font-normal">(optional)</span>
              </label>
              <input type="text" value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="dQw4w9WgXcQ" className={inp} />
              <p className="text-gray-700 text-xs mt-1">The part after youtube.com/watch?v=</p>
            </div>

            {blockedNoDj ? (
              <div className="text-center py-2">
                <p className="text-gray-400 text-sm mb-3">You need a DJ profile to add a DJ set. Adding a concert or other live show? Switch the Type above.</p>
                <Link to="/request-artist"
                  className="inline-block px-5 py-2 bg-brand-600 hover:bg-brand-500 text-white font-bold rounded-xl text-sm transition-all duration-200 hover:scale-105 shadow-glow">
                  Request an Artist Profile
                </Link>
              </div>
            ) : artistMatch ? (
              <div className="px-4 py-3.5 bg-white/[0.04] border border-brand-500/30 rounded-xl">
                <p className="text-white text-sm mb-3">
                  Did you mean <span className="font-bold">{artistMatch.name}</span>?
                </p>
                <div className="flex gap-2">
                  <button type="button" onClick={confirmMatch} disabled={loading}
                    className="flex-1 btn-primary disabled:opacity-40 text-sm font-semibold py-2 rounded-lg transition-all duration-200">
                    Yes, use that
                  </button>
                  <button type="button" onClick={rejectMatch} disabled={loading}
                    className="flex-1 bg-white/[0.06] border border-white/15 text-white text-sm font-semibold py-2 rounded-lg hover:bg-white/[0.1] transition-all duration-200 disabled:opacity-40">
                    No, create new
                  </button>
                </div>
              </div>
            ) : (
              <button type="submit" disabled={loading || checkingMatch}
                className="w-full btn-primary disabled:opacity-40 font-semibold py-2.5 rounded-xl transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-glow">
                {checkingMatch ? 'Checking…' : loading ? 'Creating…' : "Add to DECK'D"}
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, inp, type = 'text', value, onChange, placeholder, required, optional }) {
  return (
    <div>
      <label className="block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
        {label}{optional && <span className="text-gray-700 normal-case tracking-normal font-normal ml-1">(optional)</span>}
      </label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} required={required} className={inp} />
    </div>
  );
}
